import React, { useContext } from 'react';
import { useIntl } from 'react-intl';

import { Album, Song } from 'interfaces';

import { AppLocale, BAND_NAME, BAND_NAME_JP } from 'constant';

import { LanguageContext } from 'context/languageContext';

import Title from 'components/ui/Title';

import usaFlag from 'assets/us.svg';
import japanFlag from 'assets/jp.svg';

import {
  Wrapper,
  AlbumWrapper,
  SideWrapper,
  SideInfoBox,
  SectionWrapper,
  AlbumPhoto,
  AlbumDescription,
  SongsWrapper
} from './AlbumDetails.styles';
import SongsList from '../SongsList';

export interface AlbumDetailsProps {
  album: Album;
  albumNumber: number;
  internationalAlbum: boolean;
  songs: Song[];
  singles?: Song[];
  musicVideos?: Song[];
}

const AlbumDetails = ({ album, internationalAlbum, albumNumber, songs, singles }: AlbumDetailsProps) => {
  const { locale } = useContext(LanguageContext);
  const intl = useIntl();

  return (
    <Wrapper>
      <Title size="big" color="secondary">
        {
          album.typeId === 'album'
            ? intl.formatMessage({ id: 'albums.details.albumTitle' }, {
              albumNumber,
              international: album.international ? intl.formatMessage({ id: 'albums.details.internationalShort' }) : ''
            } )
            : album.typeId !== 'single'
              ? intl.formatMessage({ id: 'albums.details.miniAlbumTitle' }, { albumNumber } )
              : intl.formatMessage({ id: 'albums.details.singlesTitle' } )
        }
        {locale === AppLocale.EN ? album.englishName : album.name}
      </Title>
      <AlbumWrapper flex>
        <SideWrapper>
          <AlbumPhoto sorce={`/albums/${album.id}${album.international ? '-international' : ''}.jpg`}></AlbumPhoto>
          <SideInfoBox>
            <span>
              {intl.formatMessage({ id: 'albums.releaseDate' })}{' '}
              {intl.formatDate(new Date(album.releaseDate), {
                year: 'numeric', month: 'short', day: 'numeric'
              })}
            </span>
            {
              album.typeId !== 'single' ? (
                <img
                  src={album.international ? usaFlag : japanFlag}
                  alt={
                    album.international
                      ? intl.formatMessage({ id: 'albums.details.internationalVersion' })
                      : intl.formatMessage({ id: 'albums.details.japanVersion' })
                  }
                />
              ) : null
            }
          </SideInfoBox>
          {
            album.length ? (
              <SideInfoBox>
                {intl.formatMessage({ id: 'albums.details.albumLength.title' })}
                {album.length}
              </SideInfoBox>
            ) : null
          }
          {
            album.label ? (
              <SideInfoBox>
                {intl.formatMessage({ id: 'albums.details.label.title' })}
                <div>{album.label.home.map((el) => <span key={el}>{el}</span>)}</div>
                {album.label.international.map((el) => <span key={el}>{el}</span>)}
              </SideInfoBox>
            ) : null
          }
        </SideWrapper>
        <SectionWrapper>
          {
            songs?.length ? (
              <SongsWrapper>
                <Title size="big" color="secondary" element="h2">
                  {intl.formatMessage({ id: 'albums.details.trackList' })}
                </Title>
                <SongsList songs={songs} />
              </SongsWrapper>
            ) : null
          }
          {
            singles?.length ? (
              <SongsWrapper>
                <Title size="big" color="secondary" element="h2">
                  {intl.formatMessage({ id: 'albums.details.singles' })}
                </Title>
                <SongsList listType="singles" songs={singles} />
              </SongsWrapper>
            ) : null
          }
        </SectionWrapper>
      </AlbumWrapper>
      {
        album.descriptionId ? (
          <React.Fragment>
            <AlbumDescription>
              <Title color="primary" element="h2">
                {intl.formatMessage({ id: 'albums.descriptions.title' })}
              </Title>
              {
                album.descriptionId.map((description, index) => (
                  <p key={index}>
                    {intl.formatMessage({ id: `albums.descriptions.${description}` }, {
                      name: locale === AppLocale.EN ? album.englishName : album.name,
                      bandName: locale === AppLocale.EN ? BAND_NAME : BAND_NAME_JP,
                      releaseDate: intl.formatDate(new Date(album.releaseDate), {
                        year: 'numeric', month: 'long', day: 'numeric'
                      })
                    })}
                  </p>
                ))
              }
            </AlbumDescription>
          </React.Fragment>
        ) : null
      }
    </Wrapper>
  );
};

export default AlbumDetails;
